import { Link } from 'react-router-dom';
import Header from '@/components/feature/Header';
import Footer from '@/components/feature/Footer';
import FloatingWhatsApp from '@/components/feature/FloatingWhatsApp';
import RevealWrapper from '@/components/base/RevealWrapper';
import Breadcrumb from '@/components/feature/Breadcrumb';
import ContactFormBlock from '@/components/feature/ContactFormBlock';
import { CONTACT } from '@/mocks/contactInfo';
import { useDocumentHead } from '@/hooks/useDocumentHead';
import { localBusiness, website, service, faqPage, breadcrumb, article } from '@/lib/seo';

export default function ArticleRavets() {
  useDocumentHead({
    title: 'Comment se débarrasser des ravets : le guide guadeloupéen',
    description: 'Ravets dans la cuisine, gros ravets volants : ce qui marche vraiment, ce qui aggrave le problème, et quand appeler un professionnel. Guide complet.',
    jsonLd: [article({ headline: 'Comment se débarrasser des ravets : le guide guadeloupéen', description: 'Ravets dans la cuisine, gros ravets volants : ce qui marche, ce qui aggrave, quand appeler un pro.', path: '/blogue/se-debarrasser-des-ravets' }), breadcrumb([{ name: 'Accueil', path: '/' }, { name: 'Blogue', path: '/blogue' }, { name: 'Se débarrasser des ravets', path: '/blogue/se-debarrasser-des-ravets' }])],
  });

  return (
    <main>
      <Header />
      <Breadcrumb items={[{label:'Accueil',href:'/'},{label:'Blogue',href:'/blogue'},{label:'Comment se débarrasser des ravets'}]} />

      {/* BANDEAU TITRE */}
      <section className="bg-primary-500 py-12 md:py-16">
        <div className="max-w-7xl mx-auto px-4 md:px-6">
          <Link
            to="/blogue"
            className="inline-flex items-center gap-2 text-white/60 hover:text-white text-sm mb-4 transition-colors cursor-pointer whitespace-nowrap"
          >
            <i className="ri-arrow-left-line w-4 h-4 flex items-center justify-center"></i>
            Retour au blogue
          </Link>
          <h1 className="font-heading font-black text-2xl md:text-4xl text-white leading-tight max-w-3xl">
            Comment se débarrasser des ravets : le guide complet (et honnête)
          </h1>
        </div>
      </section>

      {/* CONTENU */}
      <section className="bg-white py-12 md:py-16">
        <div className="max-w-7xl mx-auto px-4 md:px-6">
          <article className="max-w-2xl mx-auto">
            <RevealWrapper>
              <div className="prose-custom space-y-8">

                {/* INTRO */}
                <p className="text-foreground-700 text-base md:text-lg leading-relaxed">
                  Si vous lisez ceci, vous venez probablement d&rsquo;allumer la lumière de la cuisine au mauvais moment. Respirez : le ravet est le nuisible n°1 de Guadeloupe, tout le monde y passe, et il existe une méthode qui marche. Voici ce qui fonctionne, ce qui ne fonctionne pas, et le moment où il faut arrêter de bricoler.
                </p>

                {/* IMAGE ILLUSTRATION */}
                <div className="w-full rounded-xl overflow-hidden">
                  <img
                    src="/images/blogue-se-debarrasser-des-ravets-1.webp"
                    alt="Cuisine créole traditionnelle avec mobilier en bois propice à l'infestation de ravets en Guadeloupe"
                    className="w-full object-cover max-h-80 md:max-h-96"
                   loading="eager" fetchPriority="high" />
                </div>

                {/* H2 - IDENTIFIEZ VOTRE RAVET */}
                <h2 className="font-heading font-bold text-xl md:text-2xl text-foreground-950 pt-4">
                  D&rsquo;abord, identifiez votre ravet
                </h2>

                <p className="text-foreground-700 text-base leading-relaxed">
                  <strong className="text-foreground-950">Le gros ravet brun-roux (3-4 cm) qui vole parfois :</strong> c&rsquo;est la blatte américaine. Elle vit <strong className="text-foreground-950">DEHORS</strong> — égouts, regards, jardins, tas de feuilles — et entre chez vous par les canalisations, les seuils et les fenêtres, surtout en saison des pluies. Bonne nouvelle : elle ne s&rsquo;installe pas forcément ; mauvaise nouvelle : d&rsquo;autres suivront tant que les accès sont ouverts.
                </p>

                <p className="text-foreground-700 text-base leading-relaxed">
                  <strong className="text-foreground-950">Le petit ravet rayé (1-1,5 cm), jamais loin de la cuisine :</strong> c&rsquo;est la blatte germanique, et là c&rsquo;est plus sérieux. Elle vit <strong className="text-foreground-950">DEDANS</strong> — moteur du frigo, dessous d&rsquo;évier, plinthes, micro-ondes — et se reproduit à toute vitesse : une femelle porte une capsule de 30 à 40 œufs. Si vous en voyez en journée, la colonie est déjà nombreuse.
                </p>

                {/* H2 - CE QUI MARCHE */}
                <h2 className="font-heading font-bold text-xl md:text-2xl text-foreground-950 pt-4">
                  Ce qui marche (à faire dès ce soir)
                </h2>

                <ol className="space-y-5 list-none pl-0">
                  <li className="flex items-start gap-4">
                    <span className="w-8 h-8 flex items-center justify-center rounded-full bg-secondary-500 text-white font-heading font-bold text-sm shrink-0 mt-0.5">1</span>
                    <div>
                      <p className="text-foreground-950 font-semibold text-base mb-1">Coupez l&rsquo;eau.</p>
                      <p className="text-foreground-700 text-sm leading-relaxed">Le ravet meurt de soif avant de mourir de faim : réparez les fuites, essuyez éviers et douches le soir, videz la coupelle du frigo, ne laissez pas la serpillière humide en boule.</p>
                    </div>
                  </li>
                  <li className="flex items-start gap-4">
                    <span className="w-8 h-8 flex items-center justify-center rounded-full bg-secondary-500 text-white font-heading font-bold text-sm shrink-0 mt-0.5">2</span>
                    <div>
                      <p className="text-foreground-950 font-semibold text-base mb-1">Coupez la nourriture.</p>
                      <p className="text-foreground-700 text-sm leading-relaxed">Boîtes hermétiques (y compris pour le pain, les céréales, les croquettes), pas de vaisselle sale la nuit, poubelle fermée sortie régulièrement, miettes aspirées.</p>
                    </div>
                  </li>
                  <li className="flex items-start gap-4">
                    <span className="w-8 h-8 flex items-center justify-center rounded-full bg-secondary-500 text-white font-heading font-bold text-sm shrink-0 mt-0.5">3</span>
                    <div>
                      <p className="text-foreground-950 font-semibold text-base mb-1">Fermez les portes d&rsquo;entrée.</p>
                      <p className="text-foreground-700 text-sm leading-relaxed">Grilles fines sur les bondes et siphons peu utilisés (ou versez de l&rsquo;eau chaque semaine dans les siphons secs — un siphon sec est une autoroute), joints aux seuils, moustiquaires.</p>
                    </div>
                  </li>
                  <li className="flex items-start gap-4">
                    <span className="w-8 h-8 flex items-center justify-center rounded-full bg-secondary-500 text-white font-heading font-bold text-sm shrink-0 mt-0.5">4</span>
                    <div>
                      <p className="text-foreground-950 font-semibold text-base mb-1">Le gel appât en points stratégiques (choisir un gel et non une bombe).</p>
                      <p className="text-foreground-700 text-sm leading-relaxed">Petites gouttes dans les charnières de placards, sous l&rsquo;évier, derrière le frigo. C&rsquo;est la seule arme grand public réellement utile contre la germanique.</p>
                    </div>
                  </li>
                  <li className="flex items-start gap-4">
                    <span className="w-8 h-8 flex items-center justify-center rounded-full bg-secondary-500 text-white font-heading font-bold text-sm shrink-0 mt-0.5">5</span>
                    <div>
                      <p className="text-foreground-950 font-semibold text-base mb-1">L&rsquo;acide borique en poudre dans les zones inaccessibles.</p>
                      <p className="text-foreground-700 text-sm leading-relaxed">Derrière les plinthes : vieux remède, toujours efficace, à tenir hors de portée des enfants et animaux.</p>
                    </div>
                  </li>
                </ol>

                {/* H2 - CE QUI NE MARCHE PAS */}
                <h2 className="font-heading font-bold text-xl md:text-2xl text-foreground-950 pt-4">
                  Ce qui ne marche PAS (et aggrave souvent le cas)
                </h2>

                <div className="space-y-4">
                  <p className="text-foreground-700 text-base leading-relaxed">
                    <strong className="text-accent-500">Les bombes insecticides :</strong> elles tuent les ravets visibles — 5 % de la colonie — et dispersent les autres vers les pièces voisines et chez les voisins. Elles rendent aussi les survivants méfiants envers les appâts, ce qui complique le traitement professionnel derrière.
                  </p>
                  <p className="text-foreground-700 text-base leading-relaxed">
                    <strong className="text-accent-500">Les répulsifs naturels seuls</strong> (feuilles de laurier, vinaigre, huiles essentielles) : aucun effet mesurable sur une colonie installée.
                  </p>
                  <p className="text-foreground-700 text-base leading-relaxed">
                    <strong className="text-accent-500">Écraser et passer à autre chose :</strong> une femelle écrasée peut libérer sa capsule d&rsquo;œufs intacte.
                  </p>
                </div>

                {/* H2 - QUAND APPELER UN PRO */}
                <h2 className="font-heading font-bold text-xl md:text-2xl text-foreground-950 pt-4">
                  Quand appeler un professionnel
                </h2>

                <p className="text-foreground-700 text-base leading-relaxed">
                  Trois situations où le bricolage ne suffira plus :
                </p>

                <ul className="space-y-3 list-none pl-0">
                  <li className="flex items-start gap-3">
                    <i className="ri-arrow-right-s-line text-secondary-500 text-lg w-5 h-5 flex items-center justify-center mt-0.5 shrink-0"></i>
                    <span className="text-foreground-700 text-base">Vous voyez des ravets en journée (colonie saturée).</span>
                  </li>
                  <li className="flex items-start gap-3">
                    <i className="ri-arrow-right-s-line text-secondary-500 text-lg w-5 h-5 flex items-center justify-center mt-0.5 shrink-0"></i>
                    <span className="text-foreground-700 text-base">Vous en trouvez dans plusieurs pièces ou dans l&rsquo;électroménager.</span>
                  </li>
                  <li className="flex items-start gap-3">
                    <i className="ri-arrow-right-s-line text-secondary-500 text-lg w-5 h-5 flex items-center justify-center mt-0.5 shrink-0"></i>
                    <span className="text-foreground-700 text-base">Le problème revient à chaque saison des pluies malgré vos efforts (accès structurels ouverts : regards, gaines, vide sanitaire).</span>
                  </li>
                </ul>

                <p className="text-foreground-700 text-base leading-relaxed">
                  Le traitement professionnel combine gel à effet domino — le ravet contaminé empoisonne le nid —, régulateurs de croissance et traitement des points d&rsquo;entrée. Résultat en quelques jours, durable plusieurs mois, pour un budget de 100 à 250 € dans la plupart des cas.
                </p>
              </div>
            </RevealWrapper>
          </article>
        </div>
      </section>

      {/* BLOC NOTRE SOLUTION */}
      <section className="bg-background-100 py-12 md:py-16">
        <RevealWrapper>
          <div className="max-w-7xl mx-auto px-4 md:px-6">
            <div className="max-w-2xl mx-auto">
              <div className="bg-secondary-100 border-l-4 border-secondary-500 rounded-r-xl p-6 md:p-8">
                <h2 className="font-heading font-bold text-xl md:text-2xl text-foreground-950 mb-4">
                  Notre solution
                </h2>
                <p className="text-foreground-700 text-base leading-relaxed mb-6">
                  Nous traitons tous les types de ravets en Guadeloupe — blatte américaine et blatte germanique — avec un protocole professionnel adapté au climat tropical et au bâti antillais. Devis gratuit, prix ferme, intervention sous 24-48 h.
                </p>
                <div className="flex flex-col sm:flex-row items-start gap-3 flex-wrap">
                  <Link
                    to="/traitement-ravets-cafards-guadeloupe"
                    className="inline-flex items-center gap-2 whitespace-nowrap px-6 py-3 rounded-lg font-semibold text-sm transition-all duration-200 bg-secondary-500 text-white hover:bg-secondary-600 cursor-pointer"
                  >
                    Traitement ravets & cafards
                    <i className="ri-arrow-right-line w-4 h-4 flex items-center justify-center"></i>
                  </Link>
                  <Link
                    to="/tarifs"
                    className="inline-flex items-center gap-2 whitespace-nowrap px-6 py-3 rounded-lg font-semibold text-sm transition-all duration-200 bg-white text-foreground-950 border border-background-300 hover:border-secondary-300 cursor-pointer"
                  >
                    Voir tous nos tarifs
                    <i className="ri-arrow-right-line w-4 h-4 flex items-center justify-center"></i>
                  </Link>
                </div>
              </div>
            </div>
          </div>
        </RevealWrapper>
      </section>

      {/* CTA FINAL */}
      <section className="bg-primary-500 py-14 md:py-16">
        <div className="max-w-7xl mx-auto px-4 md:px-6 text-center">
          <h2 className="font-heading font-bold text-2xl md:text-3xl text-white mb-8">
            Un doute sur l&rsquo;ampleur du problème ?
          </h2>
          <p className="text-white/80 text-base leading-relaxed max-w-xl mx-auto mb-8">
            Envoyez une photo sur WhatsApp, un technicien vous répond en 2 h — gratuitement.
          </p>
          <a
            href={CONTACT.whatsappHref}
            target="_blank"
            rel="noopener noreferrer"
            className="inline-flex items-center justify-center gap-2 whitespace-nowrap px-8 py-3.5 rounded-lg font-semibold text-base transition-all duration-200 bg-whatsapp-500 text-white hover:bg-whatsapp-600 cursor-pointer"
          >
            <i className="ri-whatsapp-line text-xl w-5 h-5 flex items-center justify-center"></i>
            WhatsApp direct
          </a>
        </div>
      </section>

      {/* MAILLAGE */}
      <section className="bg-background-100 py-12 md:py-16">
        <div className="max-w-7xl mx-auto px-4 md:px-6">
          <div className="max-w-2xl mx-auto space-y-3">
            <p className="text-center text-foreground-500 text-sm uppercase tracking-wider mb-4">À lire aussi</p>
            <Link
              to="/traitement-ravets-cafards-guadeloupe"
              className="block bg-white rounded-xl p-5 border border-background-200/70 hover:border-secondary-300 transition-colors cursor-pointer"
            >
              <span className="font-heading font-bold text-foreground-950 text-base">
                Notre service traitement ravets
              </span>
              <span className="block text-foreground-500 text-sm mt-1">
                Protocole professionnel, gel à effet domino, garantie de résultat
              </span>
            </Link>
            <Link
              to="/tarifs"
              className="block bg-white rounded-xl p-5 border border-background-200/70 hover:border-secondary-300 transition-colors cursor-pointer"
            >
              <span className="font-heading font-bold text-foreground-950 text-base">
                Consultez nos tarifs
              </span>
              <span className="block text-foreground-500 text-sm mt-1">
                Prix transparents, devis gratuit en 2 h
              </span>
            </Link>
            <Link
              to="/blogue"
              className="block bg-white rounded-xl p-5 border border-background-200/70 hover:border-secondary-300 transition-colors cursor-pointer"
            >
              <span className="font-heading font-bold text-foreground-950 text-base">
                Retour au blogue
              </span>
              <span className="block text-foreground-500 text-sm mt-1">
                Tous nos guides anti-nuisibles en Guadeloupe
              </span>
            </Link>
          </div>
        </div>
      </section>

      <ContactFormBlock source="blogue-ravets" />

      <Footer />
      <FloatingWhatsApp />
    </main>
  );
}